import { useEffect } from "react";
import { CommonType, DeviceDto } from "../../../types";
import { getParserConfigList } from "../../../services/parserConfigService";
import { getDeviceTypeList, updateDevice } from "../../../services/deviceService";
import { getMyDeviceGroupListForForm } from "../../../services/deviceGroupService";
import { useDeviceFormState, DeviceFormErrors, DeviceGroupOption } from "./useDeviceFormState";

export const useDeviceUpdate = (open: boolean, device: DeviceDto | undefined) => {
  const form = useDeviceFormState();
  const { selectedDeviceType, setParserConfigs, setSelectedParserConfig } = form;

  useEffect(() => {
    if (!open || !device) return;

    form.setDeviceName(device.deviceName);
    form.setSerialNumber(device.serialNumber);
    form.setDescription(device.description ?? "");
    form.setIconId(device.iconId ?? 0);
    form.setSelectedParserConfig(null);
    form.setErrors({});

    const loadDeviceTypes = async () => {
      const res = await getDeviceTypeList();
      if (!res.data.success) return;
      const types: CommonType[] = res.data.data;
      form.setDeviceTypes(types);
      form.setSelectedDeviceType(types.find(t => t.id === device.deviceTypeId) ?? null);
    };

    const loadGroups = async () => {
      const res = await getMyDeviceGroupListForForm();
      if (!res.data.success) return;
      const groups: DeviceGroupOption[] = (res.data.data as any[])
        .filter((g: any) => g.canEdit === true)
        .map((g: any) => ({ id: g.id, name: g.name }));
      form.setDeviceGroups(groups);
      form.setSelectedDeviceGroup(groups.find(g => g.id === device.deviceGroupId) ?? null);
    };

    loadDeviceTypes().catch(console.error);
    loadGroups().catch(console.error);
  }, [open, device]);

  useEffect(() => {
    if (!open) return;
    if (!selectedDeviceType) {
      setParserConfigs([]);
      setSelectedParserConfig(null);
      return;
    }

    const loadParserConfigs = async () => {
      const res = await getParserConfigList(selectedDeviceType.id);
      if (!res.data.success) return;
      const configs: CommonType[] = res.data.data;
      setParserConfigs(configs);
      setSelectedParserConfig(prev => configs.find(c => c.id === (prev?.id ?? device?.parserConfigId)) ?? null);
    };

    loadParserConfigs().catch(console.error);
  }, [selectedDeviceType, open]);

  const handleUpdate = async () => {
    const { deviceName, serialNumber, selectedParserConfig, description, iconId, selectedDeviceGroup } = form;
    const errs: DeviceFormErrors = {};
    if (!deviceName.trim()) errs.deviceName = "Cihaz adı boş olamaz.";
    else if (deviceName.trim().length > 150) errs.deviceName = "Cihaz adı en fazla 150 karakter olabilir.";
    if (!serialNumber.trim()) errs.serialNumber = "Seri numarası boş olamaz.";
    else if (serialNumber.trim().length > 150) errs.serialNumber = "Seri numarası en fazla 150 karakter olabilir.";
    if (!selectedDeviceType) errs.selectedDeviceType = "Cihaz tipi seçiniz.";
    if (!selectedParserConfig) errs.selectedParserConfig = "Parser seçiniz.";
    if (Object.keys(errs).length > 0) { form.setErrors(errs); throw new Error(""); }

    form.setErrors({});
    const response = await updateDevice({
      id: device!.id,
      deviceName,
      description,
      serialNumber,
      parserConfigId: selectedParserConfig!.id,
      deviceTypeId: selectedDeviceType!.id,
      iconId,
      deviceGroupId: selectedDeviceGroup?.id ?? null,
    });

    if (!response.data.success) throw new Error(response.data.message);
    return response.data.message;
  };

  return { ...form, handleUpdate };
};
